import { getExecEndpoint } from '../../service/getExecEndpoint';
import { banqoraPortfolioAnalysisApi } from '../servers/defaultServer';
import type { CorrelationClusterData } from '~/types/visualization';

export const portfolioAnalysisEndpoint = {
  server: banqoraPortfolioAnalysisApi,
  path: 'api/v1/portfolio-analysis/snapshots',
  description: 'Submit portfolio allocation data for analysis',
};

export const portfolioReAnalysisEndpoint = {
  server: banqoraPortfolioAnalysisApi,
  path: 'api/v1/portfolio-analysis/calculate',
  description: 'Re-analyze portfolio data',
};

export const portfolioAnalysisFeedbackEndpoint = {
  server: banqoraPortfolioAnalysisApi,
  path: 'api/v1/portfolio-analysis/feedback',
  description: 'Submit feedback for portfolio analysis',
};

export const portfolioPerformanceEndpoint = {
  server: banqoraPortfolioAnalysisApi,
  path: 'api/v1/portfolio-analysis/reports/portfolio',
  description: 'Get portfolio performance metrics and charts',
};

export const portfolioForwardProjectionEndpoint = {
  server: banqoraPortfolioAnalysisApi,
  path: 'api/v1/portfolio-analysis/reports/portfolio',
  description: 'Get forward P&L projection using EWMA-based simulation',
};

export const portfolioConstituentBreakdownEndpoint = {
  server: banqoraPortfolioAnalysisApi,
  path: 'api/v1/portfolio-analysis/reports/portfolio',
  description:
    'Get forward projection constituent breakdown with weekly timeseries',
};

export const portfolioCsvDownloadEndpoint = {
  server: banqoraPortfolioAnalysisApi,
  path: 'api/v1/portfolio-analysis/reports/portfolio',
  description: 'Download portfolio analysis CSV report',
};

export const portfolioCorrelationClusterEndpoint = {
  server: banqoraPortfolioAnalysisApi,
  path: 'api/v1/portfolio-analysis/visualizations/correlation-cluster',
  description: 'Get correlation cluster visualization data',
};

export const allocationAnalysisEndpoint = {
  server: banqoraPortfolioAnalysisApi,
  path: 'api/v1/portfolio-analysis/snapshots',
  description:
    'Get allocation analysis with hierarchical sector/holding breakdown',
};

export const portfolioHasBenchmarkEndpoint = {
  server: banqoraPortfolioAnalysisApi,
  path: 'api/v1/portfolio-analysis/portfolios',
  description: 'Check if a portfolio has a benchmark configured',
};

const endpointPortfolioAnalysisExec =
  getExecEndpoint<PortfolioAnalysisEndpointDef>(portfolioAnalysisEndpoint);

const endpointPortfolioReAnalysisExec =
  getExecEndpoint<PortfolioReAnalysisEndpointDef>(portfolioReAnalysisEndpoint);

const endpointPortfolioAnalysisFeedbackExec =
  getExecEndpoint<PortfolioAnalysisFeedbackEndpointDef>(
    portfolioAnalysisFeedbackEndpoint,
  );

const endpointPortfolioPerformanceExec =
  getExecEndpoint<PortfolioPerformanceEndpointDef>(
    portfolioPerformanceEndpoint,
  );

const endpointPortfolioForwardProjectionExec =
  getExecEndpoint<PortfolioForwardProjectionEndpointDef>(
    portfolioForwardProjectionEndpoint,
  );

const endpointPortfolioConstituentBreakdownExec =
  getExecEndpoint<PortfolioConstituentBreakdownEndpointDef>(
    portfolioConstituentBreakdownEndpoint,
  );

const endpointPortfolioCsvDownloadExec =
  getExecEndpoint<PortfolioCsvDownloadEndpointDef>(
    portfolioCsvDownloadEndpoint,
  );

const endpointPortfolioCorrelationClusterExec =
  getExecEndpoint<PortfolioCorrelationClusterEndpointDef>(
    portfolioCorrelationClusterEndpoint,
  );

const endpointAllocationAnalysisExec =
  getExecEndpoint<AllocationAnalysisEndpointDef>(allocationAnalysisEndpoint);

const endpointPortfolioHasBenchmarkExec =
  getExecEndpoint<PortfolioHasBenchmarkEndpointDef>(
    portfolioHasBenchmarkEndpoint,
  );

export const endpointPortfolioAnalysisPOST = async (
  payload: PortfolioAnalysisRequest,
) =>
  endpointPortfolioAnalysisExec<'POST'>({ method: 'POST', payload: payload });

export const endpointPortfolioReAnalysisPOST = async (
  payload: PortfolioReAnalysisRequest,
) =>
  endpointPortfolioReAnalysisExec<'POST'>({ method: 'POST', payload: payload });

export const endpointPortfolioAnalysisFeedbackPOST = async (
  payload: PortfolioAnalysisFeedbackRequest,
) =>
  endpointPortfolioAnalysisFeedbackExec<'POST'>({
    method: 'POST',
    payload: payload,
  });

export const endpointPortfolioPerformanceGET = async (
  params: PortfolioPerformanceRequest,
) => {
  const urlParams = new URLSearchParams({
    start_date: params.start_date,
    end_date: params.end_date,
  });

  if (params.include_current !== undefined) {
    urlParams.append('include_current', params.include_current.toString());
  }

  if (params.include_benchmark !== undefined) {
    urlParams.append('include_benchmark', params.include_benchmark.toString());
  }

  const urlExtra = `?${urlParams.toString()}`;
  return endpointPortfolioPerformanceExec<'GET'>({
    method: 'GET',
    urlExtra: `/${params.snapshot_id}${urlExtra}`,
  });
};

export const endpointPortfolioForwardProjectionGET = async (
  params: PortfolioForwardProjectionRequest,
) => {
  const urlParams = new URLSearchParams();

  if (params.horizon_days !== undefined) {
    urlParams.append('horizon_days', params.horizon_days.toString());
  }

  if (params.include_current !== undefined) {
    urlParams.append('include_current', params.include_current.toString());
  }

  if (params.include_benchmark !== undefined) {
    urlParams.append('include_benchmark', params.include_benchmark.toString());
  }

  if (params.seed !== undefined) {
    urlParams.append('seed', params.seed.toString());
  }

  const urlExtra = urlParams.toString() ? `?${urlParams.toString()}` : '';
  return endpointPortfolioForwardProjectionExec<'GET'>({
    method: 'GET',
    urlExtra: `/${params.snapshot_id}/forward-projection${urlExtra}`,
  });
};

export const endpointPortfolioConstituentBreakdownGET = async (
  params: PortfolioConstituentBreakdownRequest,
) => {
  const urlParams = new URLSearchParams();

  if (params.scenario !== undefined) {
    urlParams.append('scenario', params.scenario);
  }

  if (params.horizon_days !== undefined) {
    urlParams.append('horizon_days', params.horizon_days.toString());
  }

  const urlExtra = urlParams.toString() ? `?${urlParams.toString()}` : '';
  return endpointPortfolioConstituentBreakdownExec<'GET'>({
    method: 'GET',
    urlExtra: `/${params.snapshot_id}/forward-projection/constituents${urlExtra}`,
  });
};

export const endpointPortfolioCsvDownloadGET = async (snapshotId: string) => {
  return endpointPortfolioCsvDownloadExec<'GET'>({
    method: 'GET',
    urlExtra: `/${snapshotId}/csv`,
  });
};

export const endpointPortfolioXlsxDownloadGET = async (snapshotId: string) => {
  return endpointPortfolioCsvDownloadExec<'GET'>({
    method: 'GET',
    urlExtra: `/${snapshotId}/xlsx`,
    config: {
      responseType: 'blob',
    },
  });
};

export const endpointPortfolioCorrelationClusterGET = async (
  snapshotId: string,
  params?: {
    lookbackDays?: number;
    correlationThreshold?: number;
    portfolioType?: 'current' | 'proposed';
  },
) => {
  const urlParams = new URLSearchParams();

  if (params?.lookbackDays !== undefined) {
    urlParams.append('lookback_days', params.lookbackDays.toString());
  }

  if (params?.correlationThreshold !== undefined) {
    urlParams.append(
      'correlation_threshold',
      params.correlationThreshold.toString(),
    );
  }

  if (params?.portfolioType !== undefined) {
    urlParams.append('portfolio_type', params.portfolioType);
  }

  const urlExtra = urlParams.toString()
    ? `/${snapshotId}?${urlParams.toString()}`
    : `/${snapshotId}`;

  return endpointPortfolioCorrelationClusterExec<'GET'>({
    method: 'GET',
    urlExtra,
  });
};

export const endpointPortfolioHasBenchmarkGET = async (portfolioId: string) => {
  return endpointPortfolioHasBenchmarkExec<'GET'>({
    method: 'GET',
    urlExtra: `/${portfolioId}/has-benchmark`,
  });
};

export const endpointAllocationAnalysisPOST = async (
  snapshotId: string,
  payload: AllocationAnalysisRequest,
) => {
  return endpointAllocationAnalysisExec<'POST'>({
    method: 'POST',
    urlExtra: `/${snapshotId}/allocations/analysis`,
    payload,
  });
};

export interface PortfolioAnalysisEndpointDef {
  endpoint: typeof portfolioAnalysisEndpoint;
  methods: {
    POST: {
      response: PortfolioAnalysisResponse;
      payload: PortfolioAnalysisRequest;
    };
  };
}

export interface PortfolioReAnalysisEndpointDef {
  endpoint: typeof portfolioReAnalysisEndpoint;
  methods: {
    POST: {
      response: PortfolioAnalysisResponse;
      payload: PortfolioReAnalysisRequest;
    };
  };
}

export interface PortfolioAnalysisFeedbackEndpointDef {
  endpoint: typeof portfolioAnalysisFeedbackEndpoint;
  methods: {
    POST: {
      response: PortfolioAnalysisFeedbackResponse;
      payload: PortfolioAnalysisFeedbackRequest;
    };
  };
}

export interface PortfolioAllocation {
  isin: string;
  current_allocation: number;
}

export interface PortfolioAnalysisRequest {
  portfolio_id: string;
  allocations: PortfolioAllocation[];
  description: string;
  snapshot_type: 'manual';
}

export interface PortfolioReAnalysisRequest {
  portfolio_id: string;
  snapshot_id: string;
  force_recalculation?: boolean;
}

export interface PortfolioAnalysisResponse {
  snapshot_id: string;
  portfolio_id: string;
  snapshot_datetime: string;
  total_allocations: number;
  created_allocations: number;
  updated_allocations: number;
  status: string;
  excluded_instruments: string[];
}

export interface PortfolioAnalysisFeedbackRequest {
  feedback: string;
  timestamp: string;
  user_id: string;
  org_id: string;
}

export interface PortfolioAnalysisFeedbackResponse {
  success: boolean;
  message: string;
}

export interface PortfolioPerformanceEndpointDef {
  endpoint: typeof portfolioPerformanceEndpoint;
  methods: {
    GET: {
      response: PortfolioPerformanceResponse;
      params: PortfolioPerformanceRequest;
    };
  };
}

export interface PortfolioCsvDownloadEndpointDef {
  endpoint: typeof portfolioCsvDownloadEndpoint;
  methods: {
    GET: {
      response: string | Blob;
      params: { snapshotId: string };
    };
  };
}

export interface PortfolioPerformanceRequest {
  snapshot_id: string;
  start_date: string;
  end_date: string;
  include_current?: boolean;
  include_benchmark?: boolean;
}

export interface PortfolioPerformanceResponse {
  snapshot_id: string;
  portfolio_id: string;
  period: {
    start_date: string;
    end_date: string;
    days: number;
  };
  performance_metrics: {
    current_portfolio: PerformanceMetrics | null;
    proposed_portfolio: PerformanceMetrics;
    ai_recommended_portfolio: PerformanceMetrics;
  };
  daily_cumulative_returns: {
    dates: string[];
    current_portfolio: number[] | null;
    proposed_portfolio: number[];
    ai_recommended_portfolio: number[];
    summary: {
      current_total_return: number;
      proposed_total_return: number;
      ai_recommended_total_return: number;
    };
  };
  fx_metrics: {
    fx_exposure: number;
    fx_var_95: number;
    fx_volatility: number;
    currency_concentration: Record<string, number>;
  };
  allocation_summary: {
    total_allocation: number;
    cash_allocation: number;
    sector_breakdown: Record<string, number>;
  };
  benchmark_data: unknown | null;
}

export interface PerformanceMetrics {
  total_return: number;
  annualized_return: number;
  sharpe_ratio: number;
  volatility: number;
  beta: number;
  max_drawdown: number;
  var_30d_95: number;
}

export interface PortfolioCorrelationClusterEndpointDef {
  endpoint: typeof portfolioCorrelationClusterEndpoint;
  methods: {
    GET: {
      response: CorrelationClusterData;
      params: {
        snapshotId: string;
        lookbackDays?: number;
        correlationThreshold?: number;
        portfolioType?: 'current' | 'proposed';
      };
    };
  };
}

// Allocation Analysis Types
export interface AllocationAnalysisEndpointDef {
  endpoint: typeof allocationAnalysisEndpoint;
  methods: {
    POST: {
      response: AllocationAnalysisResponse;
      payload: AllocationAnalysisRequest;
    };
  };
}

export interface AllocationAnalysisRequest {
  batch_types: string[];
}

export interface AllocationAnalysisResponse {
  snapshot_id: string;
  included_scenarios: string[];
  tree_data: AllocationAnalysisTreeNode[];
}

export interface AllocationAnalysisTreeNode {
  // Hierarchy path for ag-grid tree structure
  orgHierarchy: string[];

  // Sector level data (for parent rows)
  sector?: string;
  sector_total?: number;

  // Holding level data (for child rows)
  ticker?: string;
  full_ticker?: string;
  isin?: string;
  company_name?: string;

  // Scenario data (each scenario has its own column)
  scenarios?: Record<string, AllocationScenarioData>;
}

export interface AllocationScenarioData {
  percent?: number | null;
  value?: number | null;
  quantity?: number | null;
  beta?: number | null;
  var_95?: number | null;
  cvar_95?: number | null;
  var_95_1d_impact?: number | null;
  cvar_95_1d_impact?: number | null;
  ytd_return?: number | null;
}

// Forward Projection Types
export interface PortfolioForwardProjectionEndpointDef {
  endpoint: typeof portfolioForwardProjectionEndpoint;
  methods: {
    GET: {
      response: PortfolioForwardProjectionResponse;
      params: PortfolioForwardProjectionRequest;
    };
  };
}

export interface PortfolioForwardProjectionRequest {
  snapshot_id: string;
  horizon_days?: number;
  include_current?: boolean;
  include_benchmark?: boolean;
  seed?: number;
}

export interface ProjectionPeriod {
  start_date: string;
  end_date: string;
  days: number;
}

export interface PortfolioForwardProjectionResponse {
  snapshot_id: string;
  portfolio_id: string;
  period: ProjectionPeriod;
  dates: string[];
  current_portfolio: number[] | null;
  proposed_portfolio: number[];
  proposed_portfolio_upper: number[] | null;
  proposed_portfolio_lower: number[] | null;
  benchmark: number[] | null;
  summary: {
    current_total_return: number | null;
    proposed_total_return: number;
    benchmark_total_return: number | null;
  };
  today_index: number;
  expected_annual_return_proposed: number;
  projected_volatility_proposed: number;
  current_batch_dates: string[] | null;
  proposed_batch_dates: string[];
}

// Constituent Breakdown Types
export interface PortfolioConstituentBreakdownEndpointDef {
  endpoint: typeof portfolioConstituentBreakdownEndpoint;
  methods: {
    GET: {
      response: PortfolioConstituentBreakdownResponse;
      params: PortfolioConstituentBreakdownRequest;
    };
  };
}

export interface PortfolioConstituentBreakdownRequest {
  snapshot_id: string;
  scenario?: 'proposed' | 'current_holdings' | 'benchmark' | 'universe';
  horizon_days?: number;
}

export interface ConstituentTimeseries {
  isin: string;
  ticker: string | null;
  name: string | null;
  allocation_percent: number;
  expected_annual_return: number;
  dates: string[];
  cumulative_returns: number[];
}

export interface PortfolioConstituentBreakdownResponse {
  snapshot_id: string;
  portfolio_id: string;
  period: ProjectionPeriod;
  scenario: string;
  constituents: ConstituentTimeseries[];
  portfolio_expected_period_return: number;
  portfolio_expected_annual_return: number;
  portfolio_projected_volatility: number;
}

// Has Benchmark Types
export interface PortfolioHasBenchmarkEndpointDef {
  endpoint: typeof portfolioHasBenchmarkEndpoint;
  methods: {
    GET: {
      response: PortfolioHasBenchmarkResponse;
      params: { portfolioId: string };
    };
  };
}

export interface PortfolioHasBenchmarkResponse {
  has_benchmark: boolean;
}
